/**
 * setup 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('setup:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const setupZh: TranslationDict = {

  // ======================== 设置向导 / Setup ========================
  'title': '初始化设置',
  'stepLanguage': '语言',
  'stepInstanceDefaults': '实例配置',
  'stepProfile': '资料',
  'stepApiConfig': 'API',
  'stepCreateAI': '创建AI',
  'stepSmtp': 'SMTP',
  'stepKeyPool': 'Key池',
  'stepComplete': '完成',
  'step1Title': '选择语言',
  'step1Desc': '选择界面显示语言，之后可在设置中修改',
  'step2Title': '实例默认设置',
  'step2Desc': '配置平台的全局默认值，影响所有新用户',
  'step3Title': '完善个人资料',
  'step3Desc': '设置头像和简介，让大家认识你',
  'step4Title': '配置 API',
  'step4Desc': '配置 API Key 以便与 DeepSeek 等模型服务通信',
  'step5Title': '创建首个 AI',
  'getApiKey': '获取 API Key',
  'aiCreated': 'AI 已创建',
  'aiCreatedDesc': '「{name}」已准备就绪，后续可在 AI 管理页调整详细配置',
  'aiCreateHint': '创建一个 AI 小伙伴陪你聊天',
  'aiCreateHintDesc': '会打开平台完整的创建页（预设档位、详细参数都在那儿），创建完回到这一步',
  'aiCreateButton': '创建 AI',
  'step5Desc': '创建你的第一个 AI 伙伴，开始群聊之旅',
  'step6Title': '配置 SMTP',
  'step6Desc': '设置邮件服务，开启邮箱注册和登录功能',
  'step7Title': '添加 API Key 池',
  'step7Desc': '添加一个池 Key，供全局 AI 使用',
  'stepCompleteTitle': '准备就绪！',
  'stepCompleteDesc': '所有设置已完成，开始探索 Copree 吧',
  'complete': '进入平台',
  'next': '下一步',
  'skip': '跳过',
  'back': '上一步',
  'selectedLang': '所选语言',
  'requiredHint': '请先完成当前设置',
  'avatar': '头像',
  'avatarUpload': '点击上传头像',
  'uploading': '上传中...',
  'bio': '个人简介',
  'bioPlaceholder': '写一句话介绍自己...',
  'statusText': '个性状态',
  'statusTextPlaceholder': '如"忙碌中"',
  'statusColor': '状态颜色',
  'apiBaseUrl': 'API Base URL',
  'apiBaseUrlPlaceholder': 'https://api.deepseek.com',
  'apiKey': 'API Key',
  'apiKeyPlaceholder': '输入你的 API Key',
  'apiSkip': '跳过，使用平台默认',
  'aiName': 'AI 名称',
  'aiNamePlaceholder': '给 AI 取个名字...',
  'systemPrompt': '系统提示词（可选）',
  'systemPromptPlaceholder': '描述 AI 的性格和行为...',
  'configProfile': '预设配置',
  'configProfileChat': '聊天模式',
  'configProfileImmersive': '沉浸模式',
  'configProfileDigitalLife': '数字生命',
  'instanceDefaultLang': '默认语言',
  'instanceDefaultCredit': '默认赠送额度',
  'instanceDefaultFileQuota': '默认文件配额（MB）',
  'instanceDefaultConcurrency': '默认群 AI 并发数',
  'smtpHost': 'SMTP 服务器',
  'smtpPort': '端口',
  'smtpUsername': '用户名',
  'smtpPassword': '密码',
  'smtpFromEmail': '发件人邮箱',
  'smtpFromName': '发件人名称',
  'smtpUseTls': '使用 TLS',
  'skipSmtp': '跳过，稍后配置',
  'keyPoolName': 'Key 名称',
  'keyPoolKey': 'API Key',
  'keyPoolBaseUrl': 'Base URL',
  'skipKeyPool': '跳过，稍后添加',
}

export const setupEn: TranslationDict = {
  'title': 'Initial Setup',
  'stepLanguage': 'Language',
  'stepInstanceDefaults': 'Instance',
  'stepProfile': 'Profile',
  'stepApiConfig': 'API',
  'stepCreateAI': 'Create AI',
  'stepSmtp': 'SMTP',
  'stepKeyPool': 'Key Pool',
  'stepComplete': 'Done',
  'step1Title': 'Select Language',
  'step1Desc': 'Choose your display language. You can change it later in Settings.',
  'step2Title': 'Instance Defaults',
  'step2Desc': 'Configure global defaults for this instance, affecting all new users.',
  'step3Title': 'Complete Your Profile',
  'step3Desc': 'Set up avatar and bio so others can recognize you.',
  'step4Title': 'Configure API',
  'step4Desc': 'Set up API Key to communicate with DeepSeek and other model services.',
  'step5Title': 'Create Your First AI',
  'getApiKey': 'Get API Key',
  'aiCreated': 'AI created',
  'aiCreatedDesc': '"{name}" is ready. You can fine-tune it later on the AI management page.',
  'aiCreateHint': 'Create an AI companion to chat with',
  'aiCreateHintDesc': 'Opens the full creation page (presets and detailed parameters live there), then brings you back to this step',
  'aiCreateButton': 'Create AI',
  'step5Desc': 'Create your first AI companion and start group chat adventures.',
  'step6Title': 'Configure SMTP',
  'step6Desc': 'Set up email service to enable email-based registration and login.',
  'step7Title': 'Add API Key Pool',
  'step7Desc': 'Add a pool key for all AIs to use globally.',
  'stepCompleteTitle': 'All Set!',
  'stepCompleteDesc': 'Setup complete. Start exploring Copree now!',
  'complete': 'Enter Platform',
  'next': 'Next',
  'skip': 'Skip',
  'back': 'Back',
  'selectedLang': 'Selected Language',
  'requiredHint': 'Please complete this step first',
  'avatar': 'Avatar',
  'avatarUpload': 'Click to upload avatar',
  'uploading': 'Uploading...',
  'bio': 'Bio',
  'bioPlaceholder': 'Write a short bio...',
  'statusText': 'Status',
  'statusTextPlaceholder': 'e.g., Busy',
  'statusColor': 'Status Color',
  'apiBaseUrl': 'API Base URL',
  'apiBaseUrlPlaceholder': 'https://api.deepseek.com',
  'apiKey': 'API Key',
  'apiKeyPlaceholder': 'Enter your API Key',
  'apiSkip': 'Skip, use platform default',
  'aiName': 'AI Name',
  'aiNamePlaceholder': 'Name your AI...',
  'systemPrompt': 'System Prompt (optional)',
  'systemPromptPlaceholder': 'Describe the AI...',
  'configProfile': 'Config Profile',
  'configProfileChat': 'Chat Mode',
  'configProfileImmersive': 'Immersive Mode',
  'configProfileDigitalLife': 'Digital Life',
  'instanceDefaultLang': 'Default Language',
  'instanceDefaultCredit': 'Default Gifted Credit',
  'instanceDefaultFileQuota': 'Default File Quota (MB)',
  'instanceDefaultConcurrency': 'Default Group AI Concurrency',
  'smtpHost': 'SMTP Host',
  'smtpPort': 'Port',
  'smtpUsername': 'Username',
  'smtpPassword': 'Password',
  'smtpFromEmail': 'From Email',
  'smtpFromName': 'From Name',
  'smtpUseTls': 'Use TLS',
  'skipSmtp': 'Skip, configure later',
  'keyPoolName': 'Key Name',
  'keyPoolKey': 'API Key',
  'keyPoolBaseUrl': 'Base URL',
  'skipKeyPool': 'Skip, add later',
}

export const setupJa: TranslationDict = {
  'title': '初期設定',
  'stepLanguage': '言語',
  'stepInstanceDefaults': 'インスタンス',
  'stepProfile': 'プロフィール',
  'stepApiConfig': 'API',
  'stepCreateAI': 'AI作成',
  'stepSmtp': 'SMTP',
  'stepKeyPool': 'Keyプール',
  'stepComplete': '完了',
  'step1Title': '言語を選択',
  'step1Desc': '表示言語を選択してください。後で設定から変更できます',
  'step2Title': 'インスタンスデフォルト設定',
  'step2Desc': 'インスタンス全体のデフォルト値を設定します',
  'step3Title': 'プロフィール設定',
  'step3Desc': 'アバターと自己紹介を設定して、他の人にあなたを知ってもらいましょう',
  'step4Title': 'API 設定',
  'step4Desc': 'DeepSeek などのモデルサービスと通信するための API Key を設定します',
  'step5Title': '最初の AI を作成',
  'getApiKey': 'API Key を取得',
  'aiCreated': 'AI を作成しました',
  'aiCreatedDesc': '「{name}」の準備ができました。詳細は後から AI 管理ページで調整できます。',
  'aiCreateHint': '一緒に話せる AI を作成しましょう',
  'aiCreateHintDesc': 'プリセットと詳細パラメータを備えた作成ページが開きます。作成後はこのステップに戻ります',
  'aiCreateButton': 'AI を作成',
  'step5Desc': '最初の AI コンパニオンを作成して、グループチャットを始めましょう',
  'step6Title': 'SMTP 設定',
  'step6Desc': 'メールサービスを設定して、メールでの登録・ログインを有効にします',
  'step7Title': 'API Key プール',
  'step7Desc': 'グローバルに使用するプール Key を追加します',
  'stepCompleteTitle': '準備完了！',
  'stepCompleteDesc': 'すべての設定が完了しました。Copree を探索しましょう',
  'complete': 'プラットフォームへ',
  'next': '次へ',
  'skip': 'スキップ',
  'back': '戻る',
  'selectedLang': '選択した言語',
  'requiredHint': '先にこの設定を完了してください',
  'avatar': 'アバター',
  'avatarUpload': 'クリックしてアバターをアップロード',
  'uploading': 'アップロード中...',
  'bio': '自己紹介',
  'bioPlaceholder': '簡単な自己紹介を書く...',
  'statusText': 'ステータス',
  'statusTextPlaceholder': '例：作業中',
  'statusColor': 'ステータス色',
  'apiBaseUrl': 'API Base URL',
  'apiBaseUrlPlaceholder': 'https://api.deepseek.com',
  'apiKey': 'API Key',
  'apiKeyPlaceholder': 'API Key を入力',
  'apiSkip': 'スキップ（プラットフォームデフォルト使用）',
  'aiName': 'AI 名前',
  'aiNamePlaceholder': 'AI の名前を付けてください...',
  'systemPrompt': 'システムプロンプト（任意）',
  'systemPromptPlaceholder': 'AI の性格や行動を記述...',
  'configProfile': 'プリセット設定',
  'configProfileChat': 'チャットモード',
  'configProfileImmersive': 'イマーシブモード',
  'configProfileDigitalLife': 'デジタルライフ',
  'instanceDefaultLang': 'デフォルト言語',
  'instanceDefaultCredit': 'デフォルトクレジット',
  'instanceDefaultFileQuota': 'デフォルトファイル容量（MB）',
  'instanceDefaultConcurrency': 'デフォルトグループ AI 同時実行数',
  'smtpHost': 'SMTP ホスト',
  'smtpPort': 'ポート',
  'smtpUsername': 'ユーザー名',
  'smtpPassword': 'パスワード',
  'smtpFromEmail': '送信元メール',
  'smtpFromName': '送信元名',
  'smtpUseTls': 'TLS 使用',
  'skipSmtp': 'スキップ（後で設定）',
  'keyPoolName': 'Key 名',
  'keyPoolKey': 'API Key',
  'keyPoolBaseUrl': 'Base URL',
  'skipKeyPool': 'スキップ（後で追加）',
}
